import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useFieldTheme } from 'framework/field/hooks/useFieldTheme';
import { Icon } from 'framework/Icon';
import { Seperator } from 'framework/Input/components/Seperator';

import { EMPTY_FUNCTION } from '../../constants';
import { useFocus } from '../dropdown/hooks/useFocus';
import { Field } from '../field';
import { useDebounce } from '../field/hooks/useDebounce';

import { InputContext } from './context/InputContext';
import { InputProps, TypedValue } from './types';

export const Container = ({
  placeholder = '',
  label = '',
  information = '',
  padding = '0px',
  height = '32px',
  width = '100%',
  text_align = 'left',
  background,
  type,
  left_icon,
  right_icon,
  onFocus: onFocusProp = EMPTY_FUNCTION,
  onBlur: onBlurProp = EMPTY_FUNCTION,
  onChange: onChangeProp = EMPTY_FUNCTION,
  onMouseDown: onMouseDownProp = EMPTY_FUNCTION,
  onKeyDown = EMPTY_FUNCTION,
  onClick = EMPTY_FUNCTION,
  onRemove = EMPTY_FUNCTION,
  value = '',
  auto_focus = false,
  disabled = false,
  error = false,
  debounce = 300,
  clear = false,
  theme,
  children,
}: InputProps) => {
  const [typed_value, setTypedValue] = useState<TypedValue>({
    value,
    formatted_value: '',
    float_value: undefined,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const onMouseDown = useCallback(
    (e: React.MouseEvent, position: 'label' | 'left' | 'right') => {
      e.preventDefault();
      e.stopPropagation();
      onMouseDownProp(e, position);
      inputRef?.current?.focus();
    },
    [onMouseDownProp]
  );

  const { is_typing, setTyping } = useDebounce({
    typed_value: typed_value.value,
    debounce,
    callback: () => {
      onChangeProp(typed_value);
    },
  });

  useEffect(() => {
    if (!is_typing && value !== typed_value.value) {
      setTypedValue((prev) => ({ ...prev, value }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_typing, value]);

  const { is_focused, onFocus, onBlur } = useFocus({
    ref: inputRef,
    auto_focus,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
  });

  const onChange = useCallback(
    (new_value: TypedValue) => {
      setTyping(true);
      setTypedValue(new_value);
    },
    [setTyping]
  );

  const context = useMemo(
    () => ({
      inputRef,
      type,
      typed_value: typed_value.value,
      text_align,
      placeholder,
      auto_focus,
      value,
      disabled,
      error,
      theme,
      onChange,
      onFocus,
      onKeyDown,
      onBlur,
      onRemove,
    }),
    [
      inputRef,
      type,
      typed_value.value,
      text_align,
      placeholder,
      auto_focus,
      value,
      disabled,
      error,
      theme,
      onChange,
      onKeyDown,
      onFocus,
      onBlur,
      onRemove,
    ]
  );

  const field_theme = useFieldTheme(theme?.field);

  return (
    <InputContext.Provider value={context}>
      <div style={{ width }}>
        <Field
          label={label}
          information={information}
          height={height}
          width={width}
          padding={padding}
          background={background}
          onClick={onClick}
          onMouseDown={onMouseDown}
          is_focused={is_focused}
          left_icon={left_icon}
          right_icon={right_icon}
          disabled={disabled}
          theme={theme}
          error={error}
        >
          {children}
          {clear && !disabled && typed_value.value && (
            <>
              <Icon
                theme={{ normal: field_theme.color.disabled }}
                icon={faXmark}
                width="30px"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={onRemove}
              />
              {right_icon && <Seperator background={field_theme.color.disabled} />}
            </>
          )}
        </Field>
      </div>
    </InputContext.Provider>
  );
};
